import { EventModel } from "../models/EventModel";
import { Category, VenueSummary } from "../models/EventModel";

export type SuggestionType = 'event' | 'venue' | 'category';

export type SearchSuggestion = {
    type: SuggestionType;
    label: string;
    value: string;
    groupLabel: string;
    meta?: string;
};

export function normalizeSearchText(value: string): string {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
}

function includesNormalized(haystack: string, needle: string): boolean {
    return normalizeSearchText(haystack).includes(normalizeSearchText(needle));
}

export function getCategoryAliases(category: Category): string[] {
    return String(category.aliases || '')
        .split(',')
        .map((alias) => alias.trim())
        .filter(Boolean);
}

export function categoryMatchesQuery(category: Category, query: string): boolean {
    if (includesNormalized(category.name, query)) {
        return true;
    }

    return getCategoryAliases(category).some((alias) => includesNormalized(alias, query));
}

export function findCategoryExactMatch(categories: Category[], query: string): Category | undefined {
    const normalizedQuery = normalizeSearchText(query);

    return categories.find((category) => {
        return normalizeSearchText(category.name) === normalizedQuery;
    });
}

export function getEventSuggestions(events: EventModel[], query: string, limit = 5): SearchSuggestion[] {
    const seen = new Set<string>();
    const suggestions: SearchSuggestion[] = [];

    for (const event of events) {
        if (!event?.title || seen.has(event.title)) {
            continue;
        }

        if (!includesNormalized(event.title, query)) {
            continue;
        }

        seen.add(event.title);
        suggestions.push({
            type: 'event',
            label: event.title,
            value: event.title,
            groupLabel: 'Événements',
            meta: event.venue?.name || event.locationName || undefined,
        });

        if (suggestions.length >= limit) {
            break;
        }
    }

    return suggestions;
}

export function getVenueSuggestions(venues: VenueSummary[], query: string, limit = 5): SearchSuggestion[] {
    return venues
        .filter((venue) => {
            return includesNormalized(venue.name, query) || includesNormalized(venue.slug, query);
        })
        .slice(0, limit)
        .map((venue) => ({
            type: 'venue' as const,
            label: venue.name,
            value: venue.slug,
            groupLabel: 'Lieux',
            meta: venue.venueType || undefined,
        }));
}

export function getCategorySuggestions(categories: Category[], query: string, limit = 5): SearchSuggestion[] {
    return categories
        .filter((category) => categoryMatchesQuery(category, query))
        .slice(0, limit)
        .map((category) => ({
            type: 'category' as const,
            label: category.name,
            value: category.slug,
            groupLabel: 'Catégories',
        }));
}
